// @flow
// 改自 https://github.com/marten-de-vries/evaljs/blob/master/index.js

type Node = Object;
type VarStore = { parent: ?Object, vars: Object };
type GenNode = Node => () => any;
type GenNodeWithBody = (Node, any) => () => any;

function noop () {}
const Break = {}
const Continue = {}

function Return (val) {
  this.value = val
}

class Arguments {}

function addDeclarationsToStore (declarations, varStore) {
  for (let key in declarations) {
    if (
      declarations.hasOwnProperty(key) &&
      !varStore.vars.hasOwnProperty(key)
    ) {
      varStore.vars[key] = declarations[key]()
    }
  }
}

function createVarStore (parent: *, vars: *): VarStore {
  vars = vars || {}
  return { parent, vars }
}

export class Environment {
  _globalObj: Object;
  _curDeclarations: Object;
  _curVarStore: VarStore;
  _curThis: any;

  constructor (scopes: Array<?Object>) {
    this._globalObj = Object.assign({}, ...scopes)
    this._curVarStore = createVarStore(null, this._globalObj)
    this._curDeclarations = {}
    this._curThis = this._globalObj
  }

  _getVarStore = (name: string) => {
    let store = this._curVarStore
    // eslint-disable-next-line no-cond-assign
    do {
      if (store.vars.hasOwnProperty(name)) {
        return store.vars
      }
    } while ((store = store.parent))

    // global object as fallback
    return this._globalObj
  };

  gen: GenNode = (node: Node) => {
    var resp = this._gen(node)
    addDeclarationsToStore(this._curDeclarations, this._curVarStore)
    this._curDeclarations = {}
    return resp
  };

  _gen: GenNode = node => {
    const fnName = '_gen' + node.type

    // $FlowFixMe
    const genFn = this[fnName]
    if (!genFn) {
      console.warn(node.type + ' not supported')
      return noop
    }
    return genFn(node)
  };

  _genObj: GenNode = node => {
    if (node.type === 'Identifier') {
      return this._getVarStore.bind(this, node.name)
    } else if (node.type === 'MemberExpression') {
      return this._gen(node.object)
    } else if (node.type === 'ObjectPattern') {
      return () => this._curVarStore.vars
    }
    return noop
  };

  _genName: GenNode = node => {
    if (node.type === 'Identifier') {
      return () => node.name
    } else if (node.type === 'MemberExpression') {
      // $FlowFixMe
      return this._memExprProperty(node)
    } else if (node.type === 'ObjectPattern') {
      return this._genObjectPattern(node)
    }
    return noop
  };

  _memExprProperty: GenNode = node => {
    return node.computed
      ? this._gen(node.property)
      : this._objKey(node.property)
  };

  _objKey: GenNode = node => {
    return () => {
      let key
      if (node.type === 'Identifier') {
        key = node.name
      } else {
        key = this._gen(node)()
      }

      return key
    }
  };

  _genProgram: GenNode = node => {
    let stmts = node.body.map(stmt => this._gen(stmt))
    return () => {
      let result
      for (let i = 0; i < stmts.length; i++) {
        if (stmts[i].constructor.name === 'GeneratorFunction') {
          // 未实现
        } else {
          result = stmts[i]()
        }
        if (
          result === Break ||
          result === Continue ||
          result instanceof Return
        ) {
          break
        }
      }
      return result
    }
  };

  _genExpressionStatement: GenNode = node => {
    return this._gen(node.expression)
  };

  _genLiteral: GenNode = node => {
    return () => {
      return node.value
    }
  };

  _genBinaryExpression: GenNode = node => {
    let left: any = this._gen(node.left)
    let right: any = this._gen(node.right)

    return () => {
      const a = left()
      const b = right()
      switch (node.operator) {
        case '==':
          // eslint-disable-next-line eqeqeq
          return a == b
        case '!=':
          // eslint-disable-next-line eqeqeq
          return a != b
        case '===':
          return a === b
        case '!==':
          return a !== b
        case '<':
          return a < b
        case '<=':
          return a <= b
        case '>':
          return a > b
        case '>=':
          return a >= b
        case '<<':
          return a << b
        case '>>':
          return a >> b
        case '>>>':
          return a >>> b
        case '+':
          return a + b
        case '-':
          return a - b
        case '*':
          return a * b
        case '/':
          return a / b
        case '%':
          return a % b
        case '|':
          return a | b
        case '^':
          return a ^ b
        case '&':
          return a & b
        case 'in':
          return a in b
        case 'instanceof':
          return a instanceof b
        case '||':
          return a || b
        case '&&':
          return a && b
        default:
          throw new Error(node.operator + ' operator not supported')
      }
    }
  };

  _genVariableDeclaration: GenNode = node => {
    let assignments = []
    for (let i = 0; i < node.declarations.length; i++) {
      let decl = node.declarations[i]
      this._curDeclarations[decl.id.name] = noop
      if (decl.init) {
        assignments.push({
          type: 'AssignmentExpression',
          operator: '=',
          left: decl.id,
          right: decl.init
        })
      }
    }
    return this._gen({
      type: 'BlockStatement',
      body: assignments
    })
  };

  _genBlockStatement: GenNode = this._genProgram;

  _genIdentifier: GenNode = node => {
    return () => {
      return this._getVarStore(node.name)[node.name]
    }
  };

  _genAssignmentExpression: GenNode = node => {
    let setter = {
      '=': function (obj, name, val) {
        if (typeof name === 'string') {
          return (obj[name] = val)
        }
        if (typeof name === 'object') {
          for (const key in name) {
            obj[name[key]] = val[key]
          }
        }
      },
      '+=': function (obj, name, val) {
        return (obj[name] += val)
      },
      '-=': function (obj, name, val) {
        return (obj[name] -= val)
      },
      '*=': function (obj, name, val) {
        return (obj[name] *= val)
      },
      '/=': function (obj, name, val) {
        return (obj[name] /= val)
      },
      '%=': function (obj, name, val) {
        return (obj[name] %= val)
      },
      '<<=': function (obj, name, val) {
        return (obj[name] <<= val)
      },
      '>>=': function (obj, name, val) {
        return (obj[name] >>= val)
      },
      '>>>=': function (obj, name, val) {
        return (obj[name] >>>= val)
      },
      '|=': function (obj, name, val) {
        return (obj[name] |= val)
      },
      '^=': function (obj, name, val) {
        return (obj[name] ^= val)
      },
      '&=': function (obj, name, val) {
        return (obj[name] &= val)
      }
    }[node.operator]
    let obj = this._genObj(node.left)
    let name = this._genName(node.left)
    let val = this._gen(node.right)
    return () => {
      const theObj = obj()
      const theName = name()
      return setter(theObj, theName, val())
    }
  };

  _genObjectExpression: GenNode = node => {
    // {}
    const items = {}
    node.properties.forEach(property => {
      let key = this._objKey(property.key)()
      // $FlowFixMe
      items[key] = this._gen(property.value)
    })

    return () => {
      const result = {}
      for (const key in items) {
        result[key] = items[key]()
      }
      return result
    }
  };

  _genObjectPattern: GenNode = node => {
    // {}
    const items = {}
    node.properties.forEach(property => {
      let key = this._objKey(property.key)()
      // $FlowFixMe
      items[key] = property.value.name
    })

    return () => items
  };

  _genArrayExpression: GenNode = node => {
    var items = node.elements.map(this._gen)

    return function () {
      return items.map(fn => fn())
    }

    // return () => node.elements.map(this._gen)
  };

  _genCallExpression: GenNode = node => {
    let callee
    if (node.callee.type === 'MemberExpression') {
      let obj = this._genObj(node.callee)
      let name = this._genName(node.callee)
      callee = function () {
        var theObj = obj()
        var theName = name()
        // theObj = [1,2,3]
        return theObj[theName].bind(theObj)
      }
    } else {
      callee = this._gen(node.callee)
    }
    let args = node.arguments.map(this._gen)

    return () => {
      // $FlowFixMe
      let c = callee()
      if (c == null) {
        return c
      }
      let p2 = args.map(fn => {
        const newFn = fn()
        return newFn
      })
      return c.apply(this._globalObj, p2)

      // return c.apply(this._globalObj, args.map(arg => arg()))
    }
  };

  _genFunctionExpression: GenNode = node => {
    let self = this
    let oldDeclarations = this._curDeclarations
    this._curDeclarations = {}
    let body = this._gen(node.body)
    let declarations = this._curDeclarations
    this._curDeclarations = oldDeclarations

    // reset let store
    return () => {
      let parent = this._curVarStore

      return function () {
        // build arguments object
        let args = new Arguments()
        // $FlowFixMe
        args.length = arguments.length
        for (let i = 0; i < arguments.length; i++) {
          // $FlowFixMe
          args[i] = arguments[i]
        }

        // switch interpreter 'stack'
        let oldStore = self._curVarStore
        var oldThis = self._curThis
        self._curVarStore = createVarStore(parent)
        self._curThis = this

        addDeclarationsToStore(declarations, self._curVarStore)
        self._curVarStore.vars.arguments = args

        // add function args to let store
        node.params.forEach((param, i) => {
          // $FlowFixMe
          self._curVarStore.vars[param.name] = args[i]
        })

        let result = body()

        // switch 'stack' back
        self._curThis = oldThis
        self._curVarStore = oldStore

        if (result instanceof Return) {
          return result.value
        } else {
          return result
        }
      }
    }
  };

  _genReturnStatement: GenNode = node => {
    // return () => node.argument && new Return(this._gen(node.argument))
    var arg = node.argument ? this._gen(node.argument) : noop
    return () => {
      return new Return(arg())
    }
  };

  _genArrowFunctionExpression: GenNode = node => {
    return this._genFunctionExpression(node)
  };

  _genFunctionDeclaration: GenNode = node => {
    this._curDeclarations[node.id.name] = this._genFunctionExpression(node)
    return noop
  };

  _genConditionalExpression: GenNode = node => {
    var test = () => {
      return this._gen(node.test)()
    }
    var consequent = this._gen(node.consequent)
    var alternate = node.alternate ? this._gen(node.alternate) : noop

    return function () {
      return test() ? consequent() : alternate()
    }
  };

  _genIfStatement: GenNode = node => {
    var test = () => {
      return this._gen(node.test)()
    }
    var consequent = this._gen(node.consequent)
    var alternate = node.alternate ? this._gen(node.alternate) : () => noop

    return function () {
      var result = test() ? consequent() : alternate()
      return result
    }
  };

  _genForStatement: GenNodeWithBody = (node, body) => {
    var self = this
    var init = node.init ? this._gen(node.init) : () => noop
    var test = node.test
      ? function () {
        return self._gen(node.test)()
      }
      : function () {
        return true
      }
    var update = node.update ? this._gen(node.update) : noop
    body = body || this._gen(node.body)

    return function () {
      var resp
      for (init(); test(); update()) {
        // $FlowFixMe
        var newResp = body()

        if (newResp === Break) {
          break
        }
        if (newResp === Continue) {
          continue
        }
        resp = newResp
        if (newResp instanceof Return) {
          break
        }
      }
      return resp
    }
  };

  _genWhileStatement: GenNode = this._genForStatement;

  _genUpdateExpression: GenNode = node => {
    var update = {
      '--true': function (obj, name) {
        return --obj[name]
      },
      '--false': function (obj, name) {
        return obj[name]--
      },
      '++true': function (obj, name) {
        return ++obj[name]
      },
      '++false': function (obj, name) {
        return obj[name]++
      }
    }[node.operator + node.prefix]
    var obj = this._genObj(node.argument)
    var name = this._genName(node.argument)
    return function () {
      return update(obj(), name())
    }
  };

  _genLogicalExpression: GenNode = this._genBinaryExpression;

  _genUnaryExpression: GenNode = node => {
    if (node.operator === 'delete') {
      return this._genDelete(node)
    }
    var a = this._gen(node.argument)
    var op = {
      '-': function () {
        return -a()
      },
      '+': function () {
        return +a()
      },
      '!': function () {
        return !a()
      },
      '~': function () {
        return ~a()
      },
      typeof: function () {
        return typeof a()
      },
      void: function () {
        return void a()
      }
    }[node.operator]

    return function () {
      return op()
    }
  };

  _genDelete: GenNode = node => {
    var obj = this._genObj(node.argument)
    var attr = this._genName(node.argument)

    return function () {
      return delete obj()[attr()]
    }
  };

  _genNewExpression: GenNode = node => {
    var callee = this._gen(node.callee)
    var args = node.arguments.map(this._gen)
    return function () {
      var cl = callee()
      var ar = args.map(a => a())
      if (cl === Date || cl === Array) {
        // for native Objects
        return new cl(...ar); // eslint-disable-line
      }
      var newObject = Object.create(cl.prototype)
      cl.apply(newObject, ar)
      return newObject
    }
  };

  _genMemberExpression: GenNode = node => {
    var obj = this._gen(node.object)
    var property = this._memExprProperty(node)
    return function () {
      var objValue = obj()
      return objValue == null ? undefined : objValue[property()]
    }
  };

  _genThisExpression: GenNode = node => {
    var self = this
    return function () {
      return self._curThis
    }
  };

  _genSequenceExpression: GenNode = node => {
    var exprs = node.expressions.map(this.gen)
    return function () {
      var result
      exprs.forEach(function (expr) {
        result = expr()
      })
      return result
    }
  };

  _genEmptyStatement: GenNode = node => {
    return noop
  };

  _genDoWhileStatement: GenNode = node => {
    var body = this._gen(node.body)
    var loop = this._genForStatement(node, body)

    return function () {
      body()
      loop()
    }
  };

  _genForInStatement: GenNode = node => {
    var self = this
    var right = self._gen(node.right)
    var body = self._gen(node.body)

    var left = node.left
    if (left.type === 'VariableDeclaration') {
      self._curDeclarations[left.declarations[0].id.name] = noop
      left = left.declarations[0].id
    }
    return function () {
      var resp
      for (var x in right()) {
        self._genAssignmentExpression({
          operator: '=',
          left: left,
          right: {
            type: 'Literal',
            value: x
          }
        })()
        resp = body()
      }
      return resp
    }
  };

  _genContinueStatement: GenNode = node => {
    return function () {
      return Continue
    }
  };

  _genBreakStatement = function () {
    return function () {
      return Break
    }
  };

  _genSwitchStatement: GenNode = node => {
    var self = this
    var discriminant = self._gen(node.discriminant)
    var cases = node.cases.map(function (curCase) {
      return {
        test: curCase.test ? self._gen(curCase.test) : null,
        code: self._genProgram({ body: curCase.consequent })
      }
    })

    return function () {
      var foundMatch = false
      var discriminantVal = discriminant()
      var resp, defaultCase

      for (var i = 0; i < cases.length; i++) {
        var curCase = cases[i]
        if (!foundMatch) {
          if (!curCase.test) {
            defaultCase = curCase
            continue
          }
          if (discriminantVal !== curCase.test()) {
            continue
          }
          foundMatch = true
        }
        // foundMatch is guaranteed to be true here
        var newResp = curCase.code()
        if (newResp === Break) {
          return resp
        }
        resp = newResp
        if (resp === Continue || resp instanceof Return) {
          return resp
        }
      }
      if (!foundMatch && defaultCase) {
        return defaultCase.code()
      }
    }
  };

  _genTemplateLiteral: GenNode = node => {
    var subNodes = [...node.expressions, ...node.quasis]
    subNodes.sort((n1, n2) => (n1.start > n2.start ? 1 : -1))
    var subs = subNodes.map(this._gen)
    return function () {
      var subsResult = []
      for (var i = 0; i < subs.length; i++) {
        const subNode = subs[i]
        subsResult.push(subNode())
      }
      return subsResult.join('')
    }
  };

  _genTemplateElement: GenNode = node => {
    return () => node.value.raw
  };
}



// WEBPACK FOOTER //
// ./src/frame/jsEnv/sandbox.js